// lib/GoogleTagManager/GoogleTagManagerProvider.tsx
'use client';

import { useAuthContext } from '@/hooks/use-auth';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { useMemo, createContext, useContext, useEffect } from 'react';
import { isPlayground as checkIsPlayground } from '@/utils/check-is-playground';
import TagManager from 'react-gtm-module';

type GoogleTagManagerContextValue = {
  pushToDataLayer: (data: object) => void;
};

const GoogleTagManagerContext = createContext<GoogleTagManagerContextValue>({
  pushToDataLayer: () => {},
});

type GoogleTagManagerProviderProps = {
  enabled?: boolean;
  children: React.ReactNode;
};

export const GoogleTagManagerProvider = ({ enabled = true, children }: GoogleTagManagerProviderProps) => {
  const { email } = useAuthContext();
  const { gtmId } = useEnvironmentContext();
  const isPlaygroundMode = useMemo(() => checkIsPlayground(), []);
  const isNebulyUser = email?.endsWith('@nebuly.ai') || false;
  useEffect(() => {
    if (!enabled || !gtmId) {
      return;
    }
    if (!isPlaygroundMode && isNebulyUser) {
      return;
    }

    TagManager.initialize({ gtmId: gtmId });
  }, [gtmId, enabled, isNebulyUser, isPlaygroundMode]);

  const pushToDataLayer = (data: object) => {
    TagManager.dataLayer({ dataLayer: data });
  };

  return <GoogleTagManagerContext.Provider value={{ pushToDataLayer }}>{children}</GoogleTagManagerContext.Provider>;
};

export const useGoogleTagManager = () => useContext(GoogleTagManagerContext);
